import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import {
    DayOfWeek,
    DefaultButton,
    Icon,
    MessageBar,
    MessageBarType,
    PanelType,
    PrimaryButton
} from "office-ui-fabric-react";
import DirtyPanel from "../../../../common/DirtyPanel";
import { getContentAreaPanelWidth } from "../../../../common/ExtensionPanel/ExtensionPanel";
import { TInlineEditProps } from "../../../../common/extensibleEntity/EntityDetailsList";
import { IInputProps } from "../../../../common/interfaces/IInputProps";
import { TimeTrackingPersistTimelineList } from "../../../../timeTracking/TimeTrackingPersistTimelineList";
import { TimeTrackingCellValueType } from "../../../../timeTracking/cells/TimeTrackingCell";
import { WithEntityType } from "../../../../timeTracking/TimeTrackingGrid";
import { BuildEntriesTimeTrackingLines, BuildProjectLines, ControlSettings } from "../../../../timeTracking/common";
import { IScaleTimelineSegment } from "../../../../common/timeline/TimelineSegment";
import { getDaysBetweenDates } from "../../../../common/timeline/utils";
import EntityPicker, { EntityInfo } from "../../../../common/inputs/EntityPicker";
import { ApplicationState } from "../../../../../store";
import { getFirstDayOfWeekSetting } from "../../../../../store/selectors/settingsSelectors";
import { TimeTrackingAdministrativeCategory } from "../../../../../store/Tenant";
import { TimeTrackingLine } from "../../../../../store/TimeTrackingStore";
import {
    TimesheetEditApplyResult,
    TimesheetEditMappingItem,
    TimesheetEditPrepareResult,
    TimesheetEditRow,
    TimesheetEditRowKind,
    TimesheetEditTarget,
    TimesheetEditTargetLine,
    TimesheetEditValidateResult,
    TimesheetEditWarning
} from "../../../../../store/TimesheetApprovalStore";
import { Dictionary, EntityType, ITimeframe } from "../../../../../entities/common";
import { Field, FieldGroup, FieldType } from "../../../../../entities/Metadata";
import { Views } from "../../../../../store/services/viewSaver";
import { findProjectTasks } from "../../../../task/taskFetchers";
import { AdministrativeTimeLabel, getTimesheetLineName } from "../timesheetApprovalFields";
import { CustomEntryBadge, FieldLabel, SourceProjectField, TargetProjectPicker, WarningIcon } from "./EditControls";
import {
    ProjectInfo,
    customOptionKey,
    groupWarningsByRow,
    projectWorkLabel,
    projectWorkOptionKey,
    resolveWarningText,
    validateDebounceMs
} from "./common";
import "./EditApprovalRequestPanel.css";
import "../TimesheetRequestGrid.css";

interface OwnProps {
    requestId: string;
    prepared: TimesheetEditPrepareResult;
    editValidate: (
        requestId: string,
        targetLine: TimesheetEditTargetLine,
        mapping: TimesheetEditMappingItem[],
        callback?: (result: TimesheetEditValidateResult) => void
    ) => void;
    editApply: (
        requestId: string,
        targetLine: TimesheetEditTargetLine,
        mapping: TimesheetEditMappingItem[],
        callback?: (result: TimesheetEditApplyResult) => void,
        onError?: () => void
    ) => void;
    onApplied: (result: TimesheetEditApplyResult) => void;
    onClose: () => void;
}

interface StateProps {
    expandSideBar: boolean;
    administrativeCategories: TimeTrackingAdministrativeCategory[];
    firstDayOfWeek: DayOfWeek;
}

type Props = OwnProps & StateProps;

type DayHours = { sourceHours?: number; targetHours?: number };

const EditApprovalRequestPanel = (props: Props) => {
    const { requestId, prepared, editValidate, editApply, onApplied, onClose } = props;
    const { expandSideBar, administrativeCategories, firstDayOfWeek } = props;
    const rows = prepared.rows ?? [];
    const sourceName = getTimesheetLineName(prepared.sourceLine.isAdministrative, prepared.sourceProject);

    const [target, setTarget] = useState<ProjectInfo | null>(null);
    const [rowTargets, setRowTargets] = useState<Dictionary<EntityInfo>>({});
    const [hours, setHours] = useState<Dictionary<Dictionary<DayHours>>>({});
    const [warnings, setWarnings] = useState<TimesheetEditWarning[]>([]);
    const [canApply, setCanApply] = useState(false);
    const [isApplying, setIsApplying] = useState(false);

    const targetLine = useMemo(() => toTargetLine(target), [target]);

    const timeFrame: ITimeframe = useMemo(
        () => ({ start: prepared.period.startDate, end: prepared.period.endDate }),
        [prepared.period]
    );
    const days = useMemo(() => getDaysBetweenDates(timeFrame.start, timeFrame.end), [timeFrame]);

    const lines = useMemo(
        () =>
            BuildEntriesTimeTrackingLines(
                prepared.timeEntries?.entries ?? [],
                administrativeCategories,
                timeFrame,
                prepared.timeEntries?.tasks ?? [],
                prepared.timeEntries?.projects ?? [],
                null
            ),
        [prepared.timeEntries, administrativeCategories, timeFrame]
    );

    const rowByLineId = useMemo(() => {
        const result: Dictionary<TimesheetEditRow> = {};
        for (const line of lines) {
            const row = rows.find(_ => line.TimeEntries.some(entry => _.entryIds.includes(entry.id)));
            if (row) {
                result[line.id] = row;
            }
        }
        return result;
    }, [lines, rows]);

    const reportedByRow = useMemo(() => {
        const result: Dictionary<Dictionary<number>> = {};
        for (const line of lines.filter(_ => rowByLineId[_.id])) {
            const reported = (result[rowByLineId[line.id].key] = result[rowByLineId[line.id].key] ?? {});
            for (const entry of line.TimeEntries) {
                const key = dayKey(entry.attributes.Date as Date);
                reported[key] = (reported[key] ?? 0) + (entry.attributes.Duration ?? 0);
            }
        }
        return result;
    }, [lines, rowByLineId]);

    useEffect(() => {
        setRowTargets(targetLine && !targetLine.isAdministrative ? buildDefaultRowTargets(rows) : {});
        setHours({});
    }, [targetLine]);

    const mappingItems = useMemo(
        (): TimesheetEditMappingItem[] =>
            rows
                .filter(row => rowTargets[row.key])
                .map(row => ({
                    sourceRowKey: row.key,
                    target: toEditTarget(rowTargets[row.key], row, !!targetLine?.isAdministrative),
                    hoursByDay: days.map(day => ({
                        date: dayKey(day),
                        sourceHours:
                            hours[row.key]?.[dayKey(day)]?.sourceHours ?? reportedByRow[row.key]?.[dayKey(day)] ?? 0,
                        targetHours: hours[row.key]?.[dayKey(day)]?.targetHours ?? 0
                    }))
                })),
        [rows, rowTargets, targetLine, days, hours, reportedByRow]
    );

    useEffect(() => {
        if (!targetLine) {
            setWarnings([]);
            setCanApply(false);
            return undefined;
        }
        const handle = setTimeout(
            () =>
                editValidate(requestId, targetLine, mappingItems, result => {
                    setWarnings(result.warnings);
                    setCanApply(result.canApply);
                }),
            validateDebounceMs
        );
        return () => clearTimeout(handle);
    }, [targetLine, mappingItems, requestId, editValidate]);

    const { gridLines, gridProjectLines } = useMemo(() => {
        const warningsByRow = groupWarningsByRow(warnings);
        const taskLines = lines.map(line => {
            const row = rowByLineId[line.id];
            return row
                ? {
                      ...line,
                      attributes: {
                          ...line.attributes,
                          Target: rowTargets[row.key],
                          TargetWarnings: rowWarningTexts(rowTargets[row.key], warningsByRow[row.key] ?? []),
                          EditedHours: hours[row.key],
                          IsCustomRow: row.kind === TimesheetEditRowKind.Custom,
                          CanSelectTarget: !!targetLine
                      }
                  }
                : line;
        });
        const projectLines = BuildProjectLines(taskLines).map(line => ({
            ...line,
            attributes: { ...line.attributes, Target: target, TargetWarnings: undefined, IsCustomRow: false }
        }));
        return { gridLines: taskLines, gridProjectLines: projectLines };
    }, [lines, rowByLineId, rowTargets, warnings, hours, target, targetLine]);

    // the grid builds columns once, so edit callbacks must read the selection from a ref
    const selection = useRef({ targetLine, rowTargets });
    selection.current = { targetLine, rowTargets };

    const categoryOptions = useMemo(
        () => administrativeCategories.filter(_ => _.isEnabled).map(_ => ({ id: _.id, name: _.title })),
        [administrativeCategories]
    );

    const resolveTargets = useCallback(
        (filter: string, line: TimeTrackingLine): EntityInfo[] | Promise<EntityInfo[]> => {
            const { targetLine: currentLine, rowTargets: currentTargets } = selection.current;
            const usedByOtherRows = otherRowTargetIds(currentTargets, rowByLineId[line.id].key);
            const available = (options: EntityInfo[]) => options.filter(_ => !usedByOtherRows.has(_.id));

            if (currentLine?.isAdministrative) {
                return available(matching(categoryOptions, filter));
            }
            if (currentLine?.projectId) {
                return findProjectTasks(currentLine.projectId, filter).then(tasks =>
                    available([...matching(fixedTargetOptions, filter), ...tasks])
                );
            }
            return [];
        },
        [rowByLineId, categoryOptions]
    );

    const inlineEditProps = useMemo(
        (): TInlineEditProps<TimeTrackingLine> => ({
            onInlineEditComplete: (_, line, value?: EntityInfo) => {
                const rowKey = rowByLineId[line.id].key;
                if (value) {
                    setRowTargets(prev => ({ ...prev, [rowKey]: value }));
                } else {
                    setRowTargets(prev => withoutKey(prev, rowKey));
                    setHours(prev => withoutKey(prev, rowKey));
                }
                return Promise.resolve();
            },
            readonlyFields: line => (selection.current.targetLine && rowByLineId[line.id] ? [] : [TargetField.name]),
            uiControlElementsCustomRender: {
                [TargetField.name]: (inputProps: IInputProps, line: TimeTrackingLine) => (
                    <EntityPicker
                        {...inputProps}
                        inputProps={{ placeholder: "Search task" }}
                        onResolveSuggestions={filter => resolveTargets(filter, line)}
                    />
                )
            }
        }),
        [rowByLineId, resolveTargets]
    );

    const isLineReadonly = useCallback((line: TimeTrackingLine) => !line.attributes.Target, []);

    const getCellValue = useCallback((line: TimeTrackingLine, segment: IScaleTimelineSegment, typeKey: string) => {
        const edited: DayHours | undefined = line.attributes.EditedHours?.[dayKey(segment.startDate)];
        return typeKey === sourceValueType.key
            ? (edited?.sourceHours ?? reportedHours(line, segment))
            : (edited?.targetHours ?? 0);
    }, []);

    const onCellEditCompleted = useCallback(
        (line: TimeTrackingLine, segment: IScaleTimelineSegment, typeKey: string, value: number) => {
            const rowKey = rowByLineId[line.id].key;
            const key = dayKey(segment.startDate);
            setHours(prev => ({
                ...prev,
                [rowKey]: {
                    ...prev[rowKey],
                    [key]: {
                        ...prev[rowKey]?.[key],
                        [typeKey === sourceValueType.key ? "sourceHours" : "targetHours"]: value
                    }
                }
            }));
        },
        [rowByLineId]
    );

    const controlSettings: ControlSettings = useMemo(
        () => ({ viewType: Views.Timeline, suggestTimeEntries: false, timeline: { timeframe: timeFrame } }),
        [timeFrame]
    );

    const apply = () => {
        if (!targetLine) {
            return;
        }
        setIsApplying(true);
        editApply(requestId, targetLine, mappingItems, onApplied, () => setIsApplying(false));
    };

    return (
        <DirtyPanel
            className="edit-approval-request-panel"
            type={PanelType.custom}
            customWidth={getContentAreaPanelWidth(expandSideBar)}
            isOpen
            isLightDismiss
            isDirty={!!target}
            primaryCommandLabel="Apply"
            cancelCommandLabel="Cancel"
            onDismiss={onClose}
            onRenderHeader={() => (
                <div className="ms-Panel-header">
                    <p className="ms-Panel-headerText">Edit Approval Request</p>
                </div>
            )}
            onRenderFooterContent={() => (
                <>
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                        This approval request will be recalled and replaced with a new Pending Approval request for the
                        target project. If the time is split between the source and target projects, it will be replaced with
                        two Pending Approval requests instead, one for each project. Both will require re-approval. The
                        corresponding timesheet will be updated accordingly.
                    </MessageBar>
                    <div className="edit-request-footer">
                        <PrimaryButton text="Apply" disabled={!canApply || isApplying} onClick={apply} />
                        <DefaultButton text="Cancel" onClick={onClose} />
                    </div>
                </>
            )}>
            <div className="edit-request-panel">
                <div className="edit-request-description">
                    Select a target project, then map source tasks to target tasks to choose what moves. Tasks left unmapped
                    keep all their time on the source. For mapped tasks, edit the hours per day to move all of a task's time
                    or split it, leaving the rest on the source.
                </div>

                <div className="edit-request-fields">
                    <SourceProjectField
                        name={sourceName}
                        tooltip="The project the approval request currently reports time to."
                    />
                    <div>
                        <FieldLabel
                            text="Target Project"
                            required
                            tooltip="The project or administrative time the selected hours will be moved to. Only projects with time tracking enabled are listed."
                        />
                        <TargetProjectPicker
                            selected={target}
                            excludeProjectId={prepared.sourceLine.projectId ?? undefined}
                            fixedOptions={prepared.sourceLine.isAdministrative ? undefined : administrativeTargets}
                            onChange={setTarget}
                        />
                    </div>
                </div>

                <div className="edit-request-legend">
                    <div className="time-type-selector">
                        <Icon iconName="Clock" />
                        <span>
                            Time Type: <span className="time-type-lbl source">Source</span>
                            <span className="time-type-lbl target">Target</span>
                        </span>
                    </div>
                </div>

                <div className="time-tracking-grid timesheet-request-grid">
                    <TimeTrackingPersistTimelineList
                        lines={gridLines}
                        projectLines={gridProjectLines}
                        timeFrame={timeFrame}
                        isReadonly={false}
                        isPeriodOpenForReporting
                        isLoading={false}
                        controlSettings={controlSettings}
                        firstDayOfWeek={firstDayOfWeek}
                        showTotalReportedColumn={false}
                        showLineMenu={false}
                        showCellTooltip={false}
                        nameColumnLabel="Source Project / Tasks"
                        additionalFields={additionalFields}
                        onAdditionalFieldRender={renderTarget}
                        onNameSuffixRender={renderNameSuffix}
                        inlineEditProps={inlineEditProps}
                        isLineReadonly={isLineReadonly}
                        isLineLocked={neverLocked}
                        cellValueTypes={cellValueTypes}
                        getCellValue={getCellValue}
                        onCellEditCompleted={onCellEditCompleted}
                    />
                </div>
            </div>
        </DirtyPanel>
    );
};

const sourceValueType: TimeTrackingCellValueType = {
    key: "source",
    title: "Source",
    className: "source",
    unit: "hours",
    emptyText: "0"
};
const targetValueType: TimeTrackingCellValueType = {
    key: "target",
    title: "Target",
    className: "target",
    unit: "hours",
    emptyText: "0"
};
const cellValueTypes = [sourceValueType, targetValueType];

const TargetField: Field = {
    id: "target",
    name: "Target",
    type: FieldType.Ref,
    isNative: false,
    isSystem: false,
    isCustom: false,
    label: "Target Project / Tasks",
    isReadonly: false,
    group: FieldGroup.SystemFields,
    settings: { views: { list: { minWidth: 260 } } }
};
const additionalFields = [TargetField];

const renderTarget = (line: TimeTrackingLine & WithEntityType, _: number, __: Field, defaultRender: () => JSX.Element) => (
    <div className={`edit-request-target ${line.entityType === EntityType.Project ? "target-line" : ""}`}>
        {defaultRender()}
        {line.entityType !== EntityType.Project && !line.attributes.Target && (
            <span className="edit-request-target-hint">
                {line.attributes.CanSelectTarget ? "Select task" : "Select Target Project first"}
            </span>
        )}
        <WarningIcon messages={line.attributes.TargetWarnings ?? []} />
    </div>
);

const renderNameSuffix = (line: TimeTrackingLine) =>
    line.attributes.IsCustomRow ? <CustomEntryBadge /> : undefined;

const administrativeTarget: ProjectInfo = { id: "__administrative__", name: AdministrativeTimeLabel };
const administrativeTargets = [administrativeTarget];

const fixedTargetOptions: EntityInfo[] = [
    { id: customOptionKey, name: "Custom Time Entry" },
    { id: projectWorkOptionKey, name: projectWorkLabel }
];

const dayKey = (date: Date) => date.toDateOnlyString();

const neverLocked = () => false;

const toTargetLine = (target: ProjectInfo | null): TimesheetEditTargetLine | null => {
    if (!target) {
        return null;
    }
    return target.id === administrativeTarget.id
        ? { isAdministrative: true }
        : { projectId: target.id, isAdministrative: false };
};

const withoutKey = <T,>(dictionary: Dictionary<T>, key: string): Dictionary<T> => {
    const result = { ...dictionary };
    delete result[key];
    return result;
};

const otherRowTargetIds = (rowTargets: Dictionary<EntityInfo>, rowKey: string) =>
    new Set(
        Object.keys(rowTargets)
            .filter(_ => _ !== rowKey)
            .map(_ => rowTargets[_].id)
    );

const matching = (options: EntityInfo[], filter: string) =>
    options.filter(_ => _.name.toLowerCase().includes(filter.toLowerCase()));

const buildDefaultRowTargets = (rows: TimesheetEditRow[]): Dictionary<EntityInfo> => {
    const result: Dictionary<EntityInfo> = {};
    for (const row of rows) {
        if (row.defaultTarget) {
            result[row.key] = fixedTargetOptions.find(
                _ =>
                    _.id ===
                    (row.defaultTarget!.kind === TimesheetEditRowKind.Custom ? customOptionKey : projectWorkOptionKey)
            )!;
        }
    }
    return result;
};

const toEditTarget = (option: EntityInfo, row: TimesheetEditRow, isAdministrativeLine: boolean): TimesheetEditTarget => {
    if (isAdministrativeLine) {
        return { kind: TimesheetEditRowKind.AdministrativeCategory, administrativeCategoryId: option.id };
    }
    if (option.id === customOptionKey) {
        return { kind: TimesheetEditRowKind.Custom, customTimeEntryName: row.label ?? "" };
    }
    if (option.id === projectWorkOptionKey) {
        return { kind: TimesheetEditRowKind.ProjectWork };
    }
    return { kind: TimesheetEditRowKind.Task, taskId: option.id };
};

const reportedHours = (line: TimeTrackingLine, segment: IScaleTimelineSegment) =>
    line.TimeEntries.filter(_ => _.attributes.Date >= segment.startDate && _.attributes.Date <= segment.finishDate).reduce(
        (sum, _) => sum + (_.attributes.Duration ?? 0),
        0
    );

const rowWarningTexts = (option: EntityInfo | undefined, warnings: TimesheetEditWarning[]) => {
    const texts = warnings.map(resolveWarningText);
    if (option?.id === customOptionKey) {
        texts.push(
            "A custom time entry will be created for the target project with the same name as the source custom time entry. This name can't be edited from this panel."
        );
    }
    return texts;
};

const mapStateToProps = (state: ApplicationState): StateProps => ({
    expandSideBar: state.user.preferences.expandSideBar,
    administrativeCategories: state.tenant.timeTracking.administrativeCategories,
    firstDayOfWeek: getFirstDayOfWeekSetting(state)
});

export default connect(mapStateToProps)(EditApprovalRequestPanel);
